import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AutoDestroy, UUID } from '@finxone-platform/shared/base-types';
import {
  baseCssConfigStyle,
  BaseCssProperties,
  BaseWidgetProperties,
  commonTextContentStyleList,
  NavigationOptionEnum,
  ProductFilterCustomerType,
  ProductFilterJourneyType,
  ProductFilters,
  ProviderClass,
  SavingsProductsCategory,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { catchError, debounceTime, map, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';

import { Router } from '@angular/router';
import { GetAvailableSavingsProducts } from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';
import { AvailableSavingsProductType } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.type';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import {
  ApiActions,
  ApiLoadingState,
  UpdateApiIsLoadingAction,
} from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import { SavingsProductsState } from '@app/finxone-web-frontend/app/lib/state/savings-products.state';
import { Select, Store } from '@ngxs/store';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { FormControl, Validators } from '@angular/forms';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { SavingsAccountService } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.service';
import {
  isValidCSS,
  transformCSSWithDynamicSelector,
} from '@app/finxone-web-frontend/app/lib/utils/themeing.utils';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-products-list-widget',
  templateUrl: './products-list-widget.component.html',
})
export class ProductsListWidgetComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy private readonly destroy$ = new Subject<void>();

  public widgetConfig: BaseWidgetProperties;
  public widgetAttributes: UiZoneWidgetAttributeConfig;
  public textContentStyle: baseCssConfigStyle;
  public productsList: AvailableSavingsProductType[] = [];
  public isApiInProgress = false;
  public isWorkflow = false;
  public appBuilderState = 'default';
  public selectedProductCode = 'default';
  public listWidgetId = 'products-list-' + new UUID().toString();

  private redirectUrl = '';
  private inputId = '';
  private pageNumber = 1;
  private pageSize = 10;
  private pagingEnabled = false;

  @Select(ApiLoadingState.getApiState(ApiActions.getSavingsProduct))
  private readonly apiLoadingState$!: Observable<{ isLoading: boolean }>;
  private hasMore: boolean;
  private hasCustomStyles = false;

  constructor(
    private readonly store: Store,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    public configService: ConfigService,
    private readonly router: Router,
    private readonly workflowFormsService: WorkflowFormsService,
    private readonly navigationService: NavigationService,
    private readonly zonePageUrlProvider: ZonePageUrlProvider,
    private readonly savingsAccountService: SavingsAccountService,
  ) {
    super();
    this.apiLoadingState$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.isApiInProgress = result.isLoading;
    });
  }

  ngOnInit(): void {
    if (this.dataIn?.attri) {
      this.widgetAttributes = this.dataIn.attri;
      this.widgetConfig = this.widgetAttributes?.widgetProperties;
      this.pagingEnabled = this.widgetConfig?.['pagingEnabled'] || false;
      this.pageSize = this.widgetConfig?.['pageSize'] || 10;
      this.fetchData();
      this.applyCustomStyles();
    }
    this.onLoadWidgetConfiguration();
  }

  ngOnDestroy(): void {
    this.removeCustomStyles();
  }

  private fetchData() {
    if (this.widgetConfig?.productFilterConfig) {
      const productType = this.widgetConfig.productFilterConfig.journeyType;
      const payload = this.createRequestPayload(productType, this.widgetConfig?.productFilterConfig);
      this.fetchProductsWithFilterPayload(payload);
    }
  }

  private fetchProductsWithFilterPayload(payload: {
    page?: number;
    limit?: number;
    category?: string;
    clientType?: string;
    accountType?: string;
  }) {
    this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsProduct, true));
    this.savingsAccountService
      .getAvailableProducts(payload)
      .pipe(
        take(1),
        map((result) => {
          this.productsList = [...this.productsList, ...result];
          this.hasMore = result.length > 0;
          this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsProduct, false));
        }),
        catchError((err) => {
          this.store.dispatch(new UpdateApiIsLoadingAction(ApiActions.getSavingsProduct, false));
          return of(err);
        }),
      )
      .subscribe();
  }

  private createRequestPayload(productType: ProductFilterJourneyType, productFilterConfig: ProductFilters) {
    return {
      page: this.pageNumber,
      limit: this.pageSize,
      category:
        productType === ProductFilterJourneyType.NEW_SAVINGS_PRODUCT
          ? SavingsProductsCategory.NEW_ACCOUNT
          : SavingsProductsCategory.RENEWAL,
      clientType:
        productFilterConfig.customerType === ProductFilterCustomerType.RETAIL ? 'personal' : 'corporate', // TODO: Use shared enum for this value
      accountType: productFilterConfig.accountType,
    };
  }

  private addControlToParentForm() {
    const currentPageForms = this.workflowFormsService.getCurrentPageForms();
    const parentForm = this.workflowFormsService.getFormPart(currentPageForms[0]);
    if (!parentForm) {
      return;
    }
    if (!parentForm.get(this.inputId)) {
      parentForm.addControl(this.inputId, new FormControl('', Validators.required));
    }
    parentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((formValues) => {
      if (formValues?.[this.inputId]) {
        this.selectedProductCode = formValues[this.inputId];
      }
    });
  }

  private onLoadWidgetConfiguration(): void {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.widgetAttributes, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BOX_SHADOW,
    ]);
    this.textContentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonTextContentStyleList,
    );
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
    this.appBuilderState = this.widgetAttributes?.widgetProperties['appBuilderPreviewOption'] ?? 'default';
    this.inputId = this.widgetAttributes?.['inputId'];
    this.addControlToParentForm();
    this.buildNavigationUrl();
  }

  public onProductClicked(product: AvailableSavingsProductType): void {
    if (!this.widgetAttributes?.['formId'] || !this.inputId) return;

    const forms = this.workflowFormsService.getCurrentPageForms();
    for (const form of forms) {
      const formPart = this.workflowFormsService.getFormPart(form);
      if (!formPart?.get(this.inputId)) continue;
      formPart?.get(this.inputId)?.setValue(product.productCode);
      this.selectedProductCode = product.productCode;
    }
    this.handleNavigation();
  }

  private applyCustomStyles() {
    this.hasCustomStyles = isValidCSS(this.widgetAttributes?.['customStyles'] ?? '');

    // return if custom styles are not valid
    if (!this.hasCustomStyles) return;

    const customStyles = this.widgetAttributes?.['customStyles'] ?? '';
    const newTextContent = transformCSSWithDynamicSelector(customStyles, `#${this.listWidgetId}`);

    // check if existing styles are the same as new one then no need to append new styles tag
    const existingStyleElement = document.getElementById(this.listWidgetId + '-styles');
    if (existingStyleElement?.textContent === newTextContent) {
      return;
    }

    // remove existing styles if they are different
    existingStyleElement?.remove();

    // append new styles
    const styleElement = document.createElement('style');
    styleElement.id = this.listWidgetId + '-styles';
    styleElement.textContent = newTextContent;
    document.head.appendChild(styleElement);
  }

  private removeCustomStyles() {
    const styleElement = document.getElementById(this.listWidgetId + '-styles');
    styleElement?.remove();
  }

  private buildNavigationUrl() {
    let pageUrl: string | undefined = this.widgetAttributes?.['pageUrl'] ?? undefined;
    const pageId: string | undefined = this.widgetAttributes?.['pageId'] ?? undefined;
    if (!pageId) {
      return;
    }
    const { pageUrl: url } = this.navigationService.buildPageNavigationUrl({
      pageId: pageId,
      zone: this.widgetAttributes?.['zone'] || '',
      pageUrl: pageUrl || '',
      role: this.widgetAttributes?.['role'] || '',
      navigationOption: this.widgetAttributes['navigationOption'],
      tabRef: this.widgetAttributes?.['tabRef'] ?? '',
      pageTransition: this.widgetAttributes?.['pageTransition'] ?? 'forward',
      browserBehaviour: this.widgetAttributes?.['browserBehaviour'],
      externalUrl: this.widgetAttributes?.['externalUrl'],
    });

    pageUrl = this.checkRouteForParamsAndUpdatePageUrl(url) || '';
    this.redirectUrl = pageUrl;
  }

  /**
   * Checks if current url has id in route params and return updated page url
   * @param pageUrl - page url from widget config
   */
  private checkRouteForParamsAndUpdatePageUrl(pageUrl: string): string | undefined {
    let url = pageUrl;
    if (this.zonePageUrlProvider?.['id']) {
      url = `${pageUrl}/${this.zonePageUrlProvider['id']}`;
    }
    return url;
  }

  private handleNavigation() {
    if (
      this.widgetAttributes?.['navigationOption'] &&
      this.widgetAttributes?.['navigationOption'] !== NavigationOptionEnum.USE_AS_BLANK
    ) {
      this.router.navigateByUrl(this.redirectUrl);
    }
  }

  public onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80 && !this.isApiInProgress && this.hasMore) {
      this.handleChildScroll();
    }
  }

  private handleChildScroll() {
    if (this.pagingEnabled) {
      this.pageNumber += 1;
      this.fetchData();
    }
  }
}
